import { ExpressionError, type Node } from './ast';
import { CONSTANTS, FUNCTIONS } from './builtins';

export type Scope = Readonly<Record<string, number>>;

export function evaluate(node: Node, scope: Scope = {}): number {
  switch (node.kind) {
    case 'number':
      return node.value;

    case 'identifier': {
      const value = CONSTANTS[node.name];
      if (value === undefined) {
        throw new ExpressionError(
          `Unknown name "${node.name}": variables are written $${node.name}`,
        );
      }
      return value;
    }

    case 'variable': {
      const value = scope[node.name];
      if (value === undefined) throw new ExpressionError(`Unknown variable "$${node.name}"`);
      return value;
    }

    case 'unary': {
      const operand = evaluate(node.operand, scope);
      return node.op === '-' ? -operand : operand;
    }

    case 'binary': {
      const left = evaluate(node.left, scope);
      const right = evaluate(node.right, scope);
      switch (node.op) {
        case '+':
          return left + right;
        case '-':
          return left - right;
        case '*':
          return left * right;
        case '/':
          return left / right;
        case '%':
          return left % right;
        case '^':
          return left ** right;
      }
      break;
    }

    case 'call': {
      const fn = FUNCTIONS[node.name];
      if (!fn) throw new ExpressionError(`Unknown function "${node.name}"`);
      const args = node.args.map((arg) => evaluate(arg, scope));
      if (fn.arity !== 'variadic' && args.length !== fn.arity) {
        throw new ExpressionError(`"${node.name}" expects ${fn.arity} argument(s)`);
      }
      return fn.apply(args);
    }
  }
}
